/**
 * Reports: what changed over a period, for everyone, a team, a squad or one developer, split by
 * project — with the bugs and the SDDs and PRDs worked in each.
 *
 * The core is what a manager is asked for every week: lines, files and commits, by project, and
 * what was worked on. The rest — prompts, cost, tests, the old breakdowns — is there to add, not to
 * wade through. One developer's report is theirs or a manager's to read; the server enforces it.
 */

import { HistoryLimitNote } from '../components/HistoryLimitNote'
import { AgentTasksReport } from '../components/TaskConversation'
import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Bug as BugIcon, FileText, FolderGit2, Info, ListChecks, Sparkles } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { formatCost, formatDuration, formatNumber } from '../lib/format'
import { useTeamFilter } from '../lib/teamFilter'
import { reportMarkdown } from '../lib/reportAnalysis'
import { Badge, CopyButton, EmptyState, ErrorBanner, Panel, ProgressBar, Segmented, Skeleton, Stat } from '../components/primitives'
import { TeamPicker } from '../components/TeamPicker'
import type { BreakdownRow, ReportItem, ReportProjectRow, ReportResult } from '../lib/types'
import { Deliveries } from '../components/Deliveries'
import { useActiveRepository } from '../lib/activeRepository'
import { Link } from 'react-router-dom'

/**
 * The AI reading of the report (41 §4.5), real: the same numbers "Copiar relatório" copies go to the person's own
 * agent through the prompt queue, with an instruction to read and answer — never to touch files. The answer arrives
 * where every answer arrives: the prompt box and the inbox. Part of the plan's prompt analysis (Pro).
 */
function AiReading({ markdown }: { markdown: string }) {
  const { t } = useI18n()
  const { active } = useActiveRepository()
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000 })
  const send = useMutation({ mutationFn: () => api.promptQueue.send(`${t('reports.ai.prompt')}\n\n${markdown}`, active?.id ?? null) })

  if (plan && !plan.entitlements.promptAnalysis) {
    return (
      <Panel title={<span className="row row--tight"><Sparkles size={15} aria-hidden /> {t('reports.ai.title')}</span>}>
        <span className="small muted">{t('reports.ai.proOnly')} <Link to="/settings/billing">{t('plans.upgrade')}</Link></span>
      </Panel>
    )
  }
  return (
    <Panel
      title={<span className="row row--tight"><Sparkles size={15} aria-hidden /> {t('reports.ai.title')}</span>}
      actions={
        <button type="button" className="btn btn--sm btn--primary" onClick={() => send.mutate()} disabled={send.isPending || send.isSuccess || !active}>
          <Sparkles size={13} /> {send.isSuccess ? t('reports.ai.sentShort') : t('reports.ai.send')}
        </button>
      }
    >
      <span className="small muted">
        {send.isSuccess ? t('reports.ai.sent') : send.isError ? t('error.generic') : active ? t('reports.ai.how') : t('reports.ai.needsRepo')}
      </span>
    </Panel>
  )
}

type Preset = 'today' | '7d' | '30d' | 'month' | 'custom'
type Extra = 'prompts' | 'cost' | 'quality' | 'breakdowns'

const EXTRAS: Extra[] = ['prompts', 'cost', 'quality', 'breakdowns']
const EXTRAS_KEY = 'devpulse:report-extras'

function iso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function rangeFor(preset: Preset): { from: string; to: string } {
  const today = new Date()
  const back = (days: number) => iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - days))
  switch (preset) {
    case 'today': return { from: iso(today), to: iso(today) }
    case '7d': return { from: back(6), to: iso(today) }
    case 'month': return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) }
    default: return { from: back(29), to: iso(today) }
  }
}

function storedExtras(): Set<Extra> {
  try {
    const value = JSON.parse(localStorage.getItem(EXTRAS_KEY) ?? '[]') as string[]
    return new Set(value.filter((v): v is Extra => (EXTRAS as string[]).includes(v)))
  } catch {
    return new Set()
  }
}

/**
 * Two views of the same work: the numbers (below), and the deliveries — per spec or per day, which agent, which
 * session, which files, what it cost (components/Deliveries.tsx, the Team plan's manager view, docs/refactor/40).
 */
export default function Reports() {
  const { t } = useI18n()
  const [view, setView] = useState<'numbers' | 'deliveries'>(() => (window.location.hash === '#entregas' ? 'deliveries' : 'numbers'))
  const tabs = (
    <Segmented<'numbers' | 'deliveries'>
      value={view}
      options={[{ value: 'numbers', label: t('reports.view.numbers') }, { value: 'deliveries', label: t('reports.view.deliveries') }]}
      onChange={(value) => {
        setView(value)
        window.history.replaceState(null, '', value === 'deliveries' ? '#entregas' : window.location.pathname)
      }}
    />
  )
  return (
    <div className="stack stack--loose reports">
      {tabs}
      {view === 'deliveries' ? <Deliveries /> : <ReportNumbers />}
    </div>
  )
}

function ReportNumbers() {
  const { t, locale } = useI18n()
  const canManage = atLeast(getSession()?.role, 'EngineeringManager')

  const [preset, setPreset] = useState<Preset>('30d')
  const [custom, setCustom] = useState(() => rangeFor('30d'))
  const { from, to } = preset === 'custom' ? custom : rangeFor(preset)

  const scope = useTeamFilter('devpulse:report-scope')
  const [onlyMine, setOnlyMine] = useState(false)
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })

  const [provider, setProvider] = useState('')
  const [taskType, setTaskType] = useState('')
  const [includeSimulated, setIncludeSimulated] = useState(false)
  const [extras, setExtras] = useState<Set<Extra>>(storedExtras)

  const toggleExtra = (extra: Extra) => {
    const next = new Set(extras)
    if (next.has(extra)) next.delete(extra)
    else next.add(extra)
    setExtras(next)
    try {
      localStorage.setItem(EXTRAS_KEY, JSON.stringify([...next]))
    } catch {
      // Storage blocked: the choice holds for this visit.
    }
  }

  const { data: providers } = useQuery({ queryKey: ['providers'], queryFn: api.settings.providers, enabled: canManage })

  const teamId = canManage ? scope.teamId : undefined
  const developerId = canManage ? scope.developerId : onlyMine ? me?.developerId ?? undefined : undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', { from, to, teamId, developerId, provider, taskType, includeSimulated }],
    queryFn: () =>
      api.reports.run({
        from,
        to,
        teamId,
        developerId,
        provider: provider || undefined,
        taskType: taskType || undefined,
        includeSimulated,
      }),
    enabled: scope.ready,
  })

  const scopeLabel = useMemo(() => {
    if (developerId) return scope.developers.find((d) => d.id === developerId)?.displayName ?? t('reports.scope.me')
    if (teamId) return scope.teams.find((team) => team.id === teamId)?.name ?? t('reports.scope.all')
    return t('reports.scope.all')
  }, [developerId, teamId, scope.developers, scope.teams, t])

  return (
    <div className="stack stack--loose">
      <HistoryLimitNote />
      <Panel title={t('common.filter')}>
        <div className="stack">
          <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
            <Segmented<Preset>
              value={preset}
              options={(['today', '7d', '30d', 'month', 'custom'] as const).map((p) => ({ value: p, label: t(`reports.preset.${p}`) }))}
              onChange={(value) => {
                if (value === 'custom') setCustom({ from, to })
                setPreset(value)
              }}
            />
            {preset === 'custom' && (
              <>
                <label className="row row--tight small">
                  <span className="muted">{t('reports.from')}</span>
                  <input className="input" type="date" value={custom.from} max={custom.to} onChange={(e) => setCustom({ ...custom, from: e.target.value })} style={{ width: 150 }} />
                </label>
                <label className="row row--tight small">
                  <span className="muted">{t('reports.to')}</span>
                  <input className="input" type="date" value={custom.to} min={custom.from} onChange={(e) => setCustom({ ...custom, to: e.target.value })} style={{ width: 150 }} />
                </label>
              </>
            )}
          </div>

          <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
            {canManage ? (
              <TeamPicker filter={scope} />
            ) : (
              me?.developerId && (
                <label className="checkbox">
                  <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
                  <span className="checkbox__text small">{t('reports.onlyMine')}</span>
                </label>
              )
            )}

            {canManage && (
              <select className="select select--sm" value={provider} onChange={(e) => setProvider(e.target.value)} aria-label={t('reports.provider')} style={{ width: 'auto' }}>
                <option value="">{t('reports.provider')}: {t('common.all')}</option>
                {(providers ?? []).map((p) => <option key={p.id} value={p.slug}>{p.displayName}</option>)}
              </select>
            )}

            <select className="select select--sm" value={taskType} onChange={(e) => setTaskType(e.target.value)} aria-label={t('reports.taskType')} style={{ width: 'auto' }}>
              <option value="">{t('reports.taskType')}: {t('common.all')}</option>
              {['Bug', 'Feature', 'TechnicalTask', 'Refactoring', 'Documentation', 'Research', 'Other'].map((type) => (
                <option key={type} value={type}>{t(`taskType.${type}`)}</option>
              ))}
            </select>

            <label className="checkbox">
              <input type="checkbox" checked={includeSimulated} onChange={(e) => setIncludeSimulated(e.target.checked)} />
              <span className="checkbox__text small">{t('reports.includeSimulated')}</span>
            </label>
          </div>

          <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
            <span className="field__label">{t('reports.include')}</span>
            {EXTRAS.map((extra) => (
              <label key={extra} className="checkbox">
                <input type="checkbox" checked={extras.has(extra)} onChange={() => toggleExtra(extra)} />
                <span className="checkbox__text small">{t(`reports.extra.${extra}`)}</span>
              </label>
            ))}
          </div>
        </div>
      </Panel>

      {/*
        Stated once, at the top, where it cannot be missed. §43 forbids ranking people; this says
        so in the product rather than only in the documentation, because a number on a screen gets
        used the way its surroundings suggest.
      */}
      <div className="row row--tight tiny muted" style={{ alignItems: 'flex-start' }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>{developerId ? t('reports.privacyNoteOne') : t('reports.privacyNote')}</span>
      </div>

      {error ? (
        <ErrorBanner message={error instanceof ApiError ? error.message : t('error.generic')} />
      ) : isLoading || !data ? (
        <div className="grid grid--stats">
          {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} height={88} />)}
        </div>
      ) : (
        <ReportBody
          data={data}
          extras={extras}
          includeSimulated={includeSimulated}
          scopeLabel={scopeLabel}
          period={t('reports.periodLine', { from, to })}
          locale={locale}
        />
      )}

      {/* Requests where one agent orchestrated others (docs/refactor/15): their conversation is part of the output. */}
      <AgentTasksReport />
    </div>
  )
}

function ReportBody({
  data,
  extras,
  includeSimulated,
  scopeLabel,
  period,
  locale,
}: {
  data: ReportResult
  extras: Set<Extra>
  includeSimulated: boolean
  scopeLabel: string
  period: string
  locale: Parameters<typeof formatNumber>[1]
}) {
  const { t } = useI18n()
  const projects = data.byProject ?? []

  const markdown = reportMarkdown(data, { title: t('reports.title'), period, scope: scopeLabel, unassigned: t('reports.noProject'), t })

  return (
    <>
      {/*
        A page of zeros looks broken, and in a fresh organization it usually is not: the
        demonstration data is simply filtered out. Saying so beats leaving someone to conclude that
        reports do not work.
      */}
      {data.workSessions === 0 && (
        <Panel>
          <EmptyState title={t('reports.noData')} body={includeSimulated ? t('reports.noDataHint') : t('reports.noDataDemoHint')} />
        </Panel>
      )}

      <div className="row row--between row--wrap">
        <div className="stack" style={{ gap: 2 }}>
          <h2 className="reports__title">{scopeLabel}</h2>
          <span className="tiny muted">{period}</span>
        </div>
        <CopyButton text={markdown} copyLabel={t('reports.copy')} copiedLabel={t('common.copied')} />
      </div>

      <div className="grid grid--stats">
        <Stat label={t('reports.linesChanged')} value={<span><span className="reports__plus">+{formatNumber(data.linesAdded, locale)}</span> <span className="reports__minus">−{formatNumber(data.linesDeleted, locale)}</span></span>} />
        <Stat label={t('reports.filesChanged')} value={formatNumber(data.filesChanged, locale)} />
        <Stat label={t('reports.commits')} value={formatNumber(data.commits ?? 0, locale)} />
        <Stat label={t('reports.workSessions')} value={formatNumber(data.workSessions, locale)} />
        <Stat label={t('reports.tasksCompleted')} value={formatNumber(data.tasksCompleted, locale)} tone="ok" />
        <Stat label={t('reports.bugsFixed')} value={formatNumber(data.bugsFixed, locale)} />
        <Stat label={t('reports.featuresCompleted')} value={formatNumber(data.featuresCompleted, locale)} />
        {extras.has('prompts') && (
          <Stat
            label={t('reports.promptsSent')}
            value={data.promptsSent == null ? '—' : formatNumber(data.promptsSent, locale)}
            foot={data.promptsSent == null ? t('reports.promptsHidden') : undefined}
          />
        )}
        {extras.has('cost') && (
          <>
            <Stat label={t('reports.tokens')} value={formatNumber(data.inputTokens + data.outputTokens, locale)} />
            <Stat
              label={t('reports.cost')}
              value={data.costAccuracy === 'Unavailable' ? '—' : formatCost(data.costUsd, locale)}
              foot={t(`cost.${data.costAccuracy}`)}
              tone="accent"
            />
          </>
        )}
        {extras.has('quality') && (
          <>
            <Stat label={t('reports.testsExecuted')} value={formatNumber(data.testsExecuted, locale)} />
            <Stat label={t('reports.testsFailed')} value={formatNumber(data.testsFailed, locale)} tone={data.testsFailed > 0 ? 'warn' : undefined} />
            <Stat label={t('reports.prsCreated')} value={formatNumber(data.pullRequestsCreated, locale)} />
            <Stat label={t('reports.rework')} value={formatNumber(data.reworkSessions, locale)} foot={t('reports.reworkHint')} tone={data.reworkSessions > 0 ? 'warn' : undefined} />
            <Stat label={t('reports.avgCycleTime')} value={data.averageCycleTimeSeconds ? formatDuration(data.averageCycleTimeSeconds, locale) : '—'} />
          </>
        )}
      </div>

      <AiReading markdown={markdown} />

      {projects.length > 0 && (
        <section className="stack">
          <h3 className="reports__section"><FolderGit2 size={15} aria-hidden /> {t('reports.byProject')}</h3>
          <div className="reports__projects">
            {projects.map((project) => (
              <ProjectCard key={project.key} project={project} total={data.linesAdded + data.linesDeleted} showPrompts={extras.has('prompts')} showCost={extras.has('cost')} locale={locale} />
            ))}
          </div>
        </section>
      )}

      {(data.byRepository ?? []).length > 0 && (
        <Panel title={t('reports.byRepository')} flush>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('reports.repository')}</th>
                  <th>{t('reports.project')}</th>
                  <th className="right">{t('reports.linesChanged')}</th>
                  <th className="right">{t('reports.filesChanged')}</th>
                  <th className="right">{t('reports.commits')}</th>
                  <th className="right">{t('reports.workSessions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.byRepository!.map((repo) => (
                  <tr key={repo.name}>
                    <td className="mono small">{repo.name}</td>
                    <td className="small">{repo.project ?? <span className="muted">{t('reports.noProject')}</span>}</td>
                    <td className="right small"><span className="reports__plus">+{formatNumber(repo.linesAdded, locale)}</span> <span className="reports__minus">−{formatNumber(repo.linesDeleted, locale)}</span></td>
                    <td className="right small">{formatNumber(repo.filesChanged, locale)}</td>
                    <td className="right small">{formatNumber(repo.commits, locale)}</td>
                    <td className="right small">{formatNumber(repo.sessions, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {extras.has('breakdowns') && (
        <div className="grid grid--two">
          <Breakdown title={t('reports.byProvider')} rows={data.byProvider} />
          <Breakdown title={t('reports.byTaskType')} rows={data.byTaskType} translateLabel />
          <Breakdown title={t('reports.byTeam')} rows={data.byTeam} />
        </div>
      )}
    </>
  )
}

function ProjectCard({
  project,
  total,
  showPrompts,
  showCost,
  locale,
}: {
  project: ReportProjectRow
  total: number
  showPrompts: boolean
  showCost: boolean
  locale: Parameters<typeof formatNumber>[1]
}) {
  const { t } = useI18n()
  const lines = project.linesAdded + project.linesDeleted
  const unassigned = project.key === 'unassigned'

  return (
    <Panel
      title={unassigned ? t('reports.noProject') : project.label}
      subtitle={project.repositories.length > 0 ? project.repositories.join(' · ') : undefined}
    >
      <div className="stack stack--tight">
        <div className="row row--wrap small" style={{ gap: 'var(--s4)' }}>
          <span><span className="reports__plus">+{formatNumber(project.linesAdded, locale)}</span> <span className="reports__minus">−{formatNumber(project.linesDeleted, locale)}</span></span>
          <span className="muted">{t('reports.filesCount', { count: project.filesChanged })}</span>
          <span className="muted">{t('reports.commitsCount', { count: project.commits })}</span>
          <span className="muted">{t('reports.sessionsCount', { count: project.sessions })}</span>
          {showPrompts && project.promptsSent != null && <span className="muted">{t('reports.promptsCount', { count: project.promptsSent })}</span>}
          {showCost && project.costUsd != null && <span className="muted">{formatCost(project.costUsd, locale)}</span>}
        </div>
        {total > 0 && <ProgressBar value={(lines / total) * 100} size="sm" />}

        <ItemList icon={<BugIcon size={13} aria-hidden />} title={t('reports.bugs')} items={project.bugs} />
        <ItemList icon={<FileText size={13} aria-hidden />} title={t('reports.specs')} items={project.specs} />
        <ItemList icon={<ListChecks size={13} aria-hidden />} title={t('reports.otherTasks')} items={project.tasks} />
        {project.bugs.length + project.specs.length + project.tasks.length === 0 && (
          <span className="tiny muted">{t('reports.noItems')}</span>
        )}
      </div>
    </Panel>
  )
}

function ItemList({ icon, title, items }: { icon: ReactNode; title: string; items: ReportItem[] }) {
  const { t } = useI18n()
  if (items.length === 0) return null

  return (
    <div className="stack" style={{ gap: 4 }}>
      <span className="field__label row row--tight">{icon} {title} · {items.length}</span>
      <ul className="reports__items">
        {items.map((item) => (
          <li key={item.id}>
            <span className="mono tiny bold">{item.key}</span>
            <span className="small truncate">{item.title}</span>
            {item.kind === 'Sdd' || item.kind === 'Prd' ? <Badge tone="info">{item.kind.toUpperCase()}</Badge> : null}
            <Badge tone={item.done ? 'ok' : 'neutral'}>{item.done ? t('reports.itemDone') : t('reports.itemOpen')}</Badge>
          </li>
        ))}
      </ul>
    </div>
  )
}


function Breakdown({ title, rows, translateLabel }: { title: string; rows: BreakdownRow[]; translateLabel?: boolean }) {
  const { t, locale } = useI18n()
  const max = Math.max(1, ...rows.map((row) => row.sessions))

  if (rows.length === 0) return null

  return (
    <Panel title={title} flush>
      <table className="table">
        <thead>
          <tr>
            <th>{title}</th>
            <th className="right">{t('reports.workSessions')}</th>
            <th className="right">{t('reports.tasksCompleted')}</th>
            <th className="right">{t('reports.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <div className="stack" style={{ gap: 4 }}>
                  <span className="small">
                    {translateLabel && row.label !== 'Unassigned' ? t(`taskType.${row.label}`) : row.label}
                  </span>
                  <ProgressBar value={(row.sessions / max) * 100} size="sm" />
                </div>
              </td>
              <td className="right small">{formatNumber(row.sessions, locale)}</td>
              <td className="right small">{formatNumber(row.tasksCompleted, locale)}</td>
              <td className="right small">{row.costUsd ? formatCost(row.costUsd, locale) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}
